import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchLabels } from '../api/vegApl';
import { searchLabels as searchLabelsApi } from '../api/vegApl/vegApl';
import { parseLabelDataPoints } from '../utils/LabelFieldMapper';
import { printViaIframe } from '../utils/printViaIframe';
import { buildMultiLabelPrintHtml } from '../components/common/PrintTemplate';
import { generateQrSvgString } from '../components/common/qrSvgGenerator';
import { useSnackbar } from '../contexts/SnackbarContext';

interface UsePrintLabelOptions {
    templateType?: string;
    defaultSite?: string;
}

export const usePrintLabel = (options: UsePrintLabelOptions = {}) => {
    const { templateType = 'Splot', defaultSite = 'MAAG' } = options;
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();

    const [printLabelOpen, setPrintLabelOpen] = useState(false);
    const [printLabelRow, setPrintLabelRow] = useState<any>(null);
    const [hasTemplate, setHasTemplate] = useState(true);
    const [foundTemplate, setFoundTemplate] = useState<any>(null);
    const [isPrinting, setIsPrinting] = useState(false);

    const { mutate: searchLabels } = useSearchLabels({
        mutation: {
            onSuccess: (data: any) => {
                const templates = data?.data ?? [];
                setHasTemplate(templates.length > 0);
                setFoundTemplate(templates[0] ?? null);
                setPrintLabelOpen(true);
            },
            onError: () => {
                setHasTemplate(false);
                setFoundTemplate(null);
                setPrintLabelOpen(true);
            },
        },
    });

    /** Open the print label dialog for a single row (searches for a matching template first) */
    const openPrintDialog = useCallback((row: Record<string, any>, siteCode?: string, cropCode?: string) => {
        setPrintLabelRow(row);
        searchLabels({
            data: {
                template_type: templateType,
                site_code: siteCode || row?.site_code || defaultSite,
                crop_code: cropCode ?? row?.crop ?? '',
            },
        });
    }, [searchLabels, templateType, defaultSite]);

    /** Open the print label dialog with a pre-resolved template (skips searchLabels call) */
    const openPrintDialogWithTemplate = useCallback((row: Record<string, any>, template: any) => {
        setPrintLabelRow(row);
        setHasTemplate(!!template);
        setFoundTemplate(template);
        setPrintLabelOpen(true);
    }, []);

    /** Close the print label dialog */
    const closePrintDialog = useCallback(() => {
        setPrintLabelOpen(false);
    }, []);

    /** Bulk print multiple rows via iframe (each row can have a different site/crop template) */
    const bulkPrint = useCallback(async (rows: Record<string, any>[], siteCode?: string, cropCode?: string) => {
        if (rows.length === 0) return;

        setIsPrinting(true);
        try {
            const fallbackSite = siteCode || defaultSite;

            // Collect unique site/crop combos to batch-fetch templates
            const uniqueKeys = new Map<string, { site_code: string; crop_code: string }>();
            for (const row of rows) {
                const rowSite = row.site_code || fallbackSite;
                const rowCrop = cropCode ?? row.crop ?? '';
                const key = `${rowSite}__${rowCrop}`;
                if (!uniqueKeys.has(key)) {
                    uniqueKeys.set(key, { site_code: rowSite, crop_code: rowCrop });
                }
            }

            // Fetch templates in parallel
            const templateCache = new Map<string, any>();
            await Promise.all(
                [...uniqueKeys.entries()].map(async ([key, params]) => {
                    try {
                        const res = await queryClient.ensureQueryData({
                            queryKey: ['searchLabels', params.site_code, params.crop_code],
                            queryFn: () => searchLabelsApi({
                                template_type: templateType,
                                site_code: params.site_code,
                                crop_code: params.crop_code,
                            }),
                        });
                        templateCache.set(key, (res?.data ?? [])[0] ?? null);
                    } catch {
                        templateCache.set(key, null);
                    }
                })
            );

            const firstTemplate = [...templateCache.values()].find(Boolean);
            if (!firstTemplate) {
                showSnackbar({ message: 'No label templates found for the selected items', severity: 'error' });
                return;
            }

            const size = firstTemplate?.size ?? '';
            const orientation = firstTemplate?.orientation ?? '';
            const copiesPerPage = firstTemplate?.default_count ?? 6; // Fallback to 6 copies per page
            const isMaterialMode = templateType?.toLowerCase() === 'material';

            const labels = rows.map((row: any) => {
                const rowSite = row.site_code || fallbackSite;
                const rowCrop = cropCode ?? row.crop ?? '';
                const key = `${rowSite}__${rowCrop}`;
                const template = templateCache.get(key);

                if (!template) {
                    return {
                        qrFields: [],
                        textFields: [{
                            key: 'error', label: 'Error',
                            value: `Error: Template not found for "${row.splot_id ?? row.matid ?? 'N/A'}"`,
                        }],
                        qrSvgHtml: '',
                    };
                }

                const { qrFields, textFields } = parseLabelDataPoints(template.data_points ?? '', row, isMaterialMode);
                const qrSvgHtml = qrFields.map((f: any) => generateQrSvgString(f.value, 128)).join('');
                return { qrFields, textFields, qrSvgHtml };
            });

            printViaIframe(buildMultiLabelPrintHtml({
                labels,
                size,
                orientation,
                isMaterialMode,
                copiesPerPage,
                leftMarginMm: isMaterialMode ? 0 : 70,
            }));
        } finally {
            setTimeout(() => setIsPrinting(false), 1000);
        }
    }, [queryClient, templateType, defaultSite, showSnackbar]);

    return {
        // State
        printLabelOpen,
        printLabelRow,
        hasTemplate,
        foundTemplate,
        isPrinting,
        // Actions
        openPrintDialog,
        openPrintDialogWithTemplate,
        closePrintDialog,
        bulkPrint,
    };
};
